import {
    PropsWithChildren,
    ReactNode,
    useState,
    useRef,
    useCallback,
    useEffect,
} from 'react'
import { createPortal } from 'react-dom'
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
} from '@floating-ui/react'
import classnames from 'classnames'
import { useDropdownZIndexStore } from '@/stores/useDropdownZIndexStore/useDropdownZIndexStore'

type Placement = 'top' | 'bottom' | 'left' | 'right'

type TooltipProps = PropsWithChildren & {
    name: string
    content: ReactNode
    className?: string
    show?: boolean
    placement?: Placement
    arrowPlacement?: Placement
}

export const Tooltip: React.FC<TooltipProps> = (props) => {
    const {
        children,
        content,
        name,
        className,
        show = true,
        placement = 'right',
        arrowPlacement = 'left',
    } = props

    const [isVisible, setIsVisible] = useState(false)
    // Initialize with high z-index to ensure tooltips appear above dropdowns (which start at 1000)
    // Use lazy initialization to compute the initial value correctly and avoid potential flash
    const [zIndex, setZIndex] = useState(() => {
        const dropdownZIndex = useDropdownZIndexStore.getState().current
        return Math.max(dropdownZIndex + 100, 10000)
    })
    const triggerRef = useRef<HTMLDivElement>(null)

    const { refs, floatingStyles } = useFloating({
        open: isVisible,
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [offset(16), flip(), shift({ padding: 8 })],
    })

    // Ensure tooltip appears above dropdowns by using z-index store
    useEffect(() => {
        if (isVisible) {
            // Get a z-index higher than current dropdowns
            const dropdownZIndex = useDropdownZIndexStore.getState().current
            setZIndex(Math.max(dropdownZIndex + 100, 10000))
        }
    }, [isVisible])

    const handleMouseEnter = useCallback(() => {
        if (!show) return
        if (triggerRef.current) {
            refs.setReference(triggerRef.current)
        }
        setIsVisible(true)
    }, [show, refs])

    const handleMouseLeave = useCallback(() => {
        setIsVisible(false)
    }, [])

    useEffect(() => {
        if (!show && isVisible) {
            setIsVisible(false)
        }
    }, [show, isVisible])

    useEffect(() => {
        if (isVisible && triggerRef.current) {
            refs.setReference(triggerRef.current)
        }
    }, [placement, isVisible, refs])

    useEffect(() => {
        const trigger = triggerRef.current
        if (!trigger) return

        trigger.addEventListener('mouseenter', handleMouseEnter)
        trigger.addEventListener('mouseleave', handleMouseLeave)

        return () => {
            trigger.removeEventListener('mouseenter', handleMouseEnter)
            trigger.removeEventListener('mouseleave', handleMouseLeave)
        }
    }, [handleMouseEnter, handleMouseLeave])

    const tooltipPortal =
        isVisible && show
            ? createPortal(
                  <div
                      ref={refs.setFloating}
                      style={{ ...floatingStyles, zIndex }}
                      className="fixed"
                      data-testid={`${name}-tooltip`}
                  >
                      <TooltipFloater
                          className={className}
                          arrowPlacement={arrowPlacement}
                          content={content}
                      />
                  </div>,
                  document.body
              )
            : null

    return (
        <>
            <div ref={triggerRef}>{children}</div>
            {tooltipPortal}
        </>
    )
}

// Type guard to validate arrow placement values
const isValidArrowPlacement = (
    placement: string | undefined | null
): placement is Placement => {
    return (
        placement === 'top' ||
        placement === 'bottom' ||
        placement === 'left' ||
        placement === 'right'
    )
}

const CLIP_WRAPPER_STYLES = {
    left: 'absolute right-full top-0 -mr-[1px] h-full w-1/2 overflow-hidden',
    right: 'absolute left-full top-0 -ml-[1px] h-full w-1/2 overflow-hidden',
    top: 'absolute bottom-full left-0 -mb-[1px] w-full h-1/2 overflow-hidden',
    bottom: 'absolute top-full left-0 -mt-[1px] w-full h-1/2 overflow-hidden',
} as const

const ARROW_POSITION_STYLES = {
    left: 'right-0 top-1/2 -translate-y-1/2 translate-x-1/2 rounded-bl-sm',
    right: 'left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-tr-sm',
    top: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rounded-tl-sm',
    bottom: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-br-sm',
} as const

export const TooltipFloater: React.FC<
    PropsWithChildren & {
        className?: string
        arrowPlacement?: Placement
        content?: ReactNode
    }
> = (props) => {
    const { children, content, className, arrowPlacement = 'left' } = props

    const validArrowPlacement: Placement = isValidArrowPlacement(arrowPlacement)
        ? arrowPlacement
        : 'left'

    const tooltipClassnames = classnames(
        'relative px-3 py-2',
        'rounded border border-neutral-200 bg-white shadow-lg',
        'text-body-small text-regular',
        'max-w-[300px]',
        className
    )

    const arrowBase = classnames(
        'absolute w-4 h-4 bg-white rotate-45',
        'border border-neutral-200 shadow-lg pointer-events-none'
    )

    const clipWrapper = CLIP_WRAPPER_STYLES[validArrowPlacement]
    const arrowPosition = ARROW_POSITION_STYLES[validArrowPlacement]

    return (
        <div className="relative">
            <div className={tooltipClassnames}>
                {content !== undefined ? (
                    typeof content === 'string' ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: content,
                            }}
                        />
                    ) : (
                        content
                    )
                ) : (
                    children
                )}
            </div>

            <div className={classnames(clipWrapper, 'pointer-events-none')}>
                <div className={classnames(arrowBase, arrowPosition)} />
            </div>
        </div>
    )
}
